import React from 'react';
import type {IProduct} from '../../../../types/galleryTypes';
import a11y from '@wix/wixstores-client-core/dist/es/src/assets/styles/_accessibility.scss';
import s from './ProductPrice.scss';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {withGlobals} from '../../../../globalPropsContext';

import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {
  formattedPrimaryPrice,
  formattedSecondaryPrice,
} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {splitFormattedAmountLabel} from '@wix/wixstores-client-storefront-common';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import classNames from 'classnames';
import {Experiments} from '../../../../constants';
import {
  isProductItemHasDiscount,
  isProductUnavailable,
  shouldRenderOutOfStock,
  shouldRenderPrice,
} from '../productItemUtils';
import {IProductItem} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';

export enum DataHook {
  SrPriceBeforeDiscount = 'sr-product-item-price-before-discount',
  SrPriceToPay = 'sr-product-item-price-to-pay',
  SrPriceRange = 'st-price-range',
  PriceBeforeDiscount = 'product-item-price-before-discount',
  PriceToPay = 'product-item-price-to-pay',
  PriceRange = 'price-range-from',
  PricesContainer = 'prices-container',
  BasePriceComponent = 'base-price-component',
  PriceRangeOriginalPriceGroup = 'price-range-original-price-group',
  PriceRangeFromPriceGroup = 'price-range-from-price-group',
}

interface TextMapProps {
  productPriceBeforeDiscountSR: string;
  productOutOfStockText: string;
  productPriceAfterDiscountSR: string;
  productPriceWhenThereIsNoDiscountSR: string;
  measurementUnits?: {[key: string]: {[key: string]: string}};
  pricePerUnitSR?: string;
}

export interface IProductPriceProps extends IGalleryGlobalProps {
  product: IProduct;
  selectedProductVariant: IProductItem;
  allowFreeProducts: boolean;
  textsMap: TextMapProps;
  fromPrice?: string;
}

const getPriceBeforeDiscount = ({
  textsMap,
  secondaryPrice,
  shouldEnableDiscountAndRegularPriceSwapViewer,
}: {
  textsMap: TextMapProps;
  secondaryPrice: string;
  shouldEnableDiscountAndRegularPriceSwapViewer: boolean;
}) => {
  return (
    <>
      <span className={a11y.srOnly} data-hook={DataHook.SrPriceBeforeDiscount}>
        {textsMap.productPriceBeforeDiscountSR}
      </span>
      <span
        data-hook={DataHook.PriceBeforeDiscount}
        className={classNames({
          [s.priceBeforeDiscount]: shouldEnableDiscountAndRegularPriceSwapViewer,
          [s.oldPriceBeforeDiscount]: !shouldEnableDiscountAndRegularPriceSwapViewer,
        })}
        data-wix-original-price={secondaryPrice}>
        {secondaryPrice}
      </span>
    </>
  );
};

class PriceRangeComp extends React.Component<
  {
    formattedFromPrice: string;
    textsMap: TextMapProps;
    product: IProduct;
    selectedVariant: IProductItem;
    clientFormattedSecondaryPrice?: string;
    clientFromPrice?: string;
  } & IProvidedTranslationProps &
    IGalleryGlobalProps
> {
  public render() {
    const {
      formattedFromPrice,
      textsMap,
      t,
      product,
      selectedVariant,
      globals,
      clientFormattedSecondaryPrice,
      clientFromPrice,
    } = this.props;
    const displayFromPrice = clientFromPrice || formattedFromPrice;
    const shouldEnableDiscountAndRegularPriceSwapViewer = globals.useExperiments.enabled(
      Experiments.EnableDiscountAndRegularPriceSwapViewer
    );
    const shouldAddDiscountsToVariantsGallery = globals.useExperiments.enabled(
      Experiments.AddDiscountsToVariantsItemsQueries
    );
    const {prefix, suffix} = splitFormattedAmountLabel(t, 'priceRangeText');
    const shouldFixPricingPrefixLayout = globals.useExperiments.enabled(Experiments.FixPricingPrefixLayout);
    const hasDiscount = isProductItemHasDiscount(product, selectedVariant);
    const shouldSplitPriceRangeGroups =
      shouldFixPricingPrefixLayout && shouldEnableDiscountAndRegularPriceSwapViewer && hasDiscount;
    const shouldRenderSecondaryPrice = !shouldFixPricingPrefixLayout || hasDiscount;

    return (
      <>
        {shouldAddDiscountsToVariantsGallery &&
          (shouldSplitPriceRangeGroups ? (
            <span data-hook={DataHook.PriceRange} className={s.priceRangeSplitLayout}>
              <span data-hook={DataHook.PriceRangeOriginalPriceGroup} className={s.priceRangeGroup}>
                {prefix}
                {getSecondaryPrice({
                  product,
                  textsMap,
                  shouldEnableDiscountAndRegularPriceSwapViewer,
                  selectedVariant,
                  clientFormattedSecondaryPrice,
                })}
              </span>
              <span data-hook={DataHook.PriceRangeFromPriceGroup} className={s.priceRangeGroup}>
                {displayFromPrice}
                {suffix}
                <span className={a11y.srOnly} data-hook={DataHook.SrPriceRange}>
                  {textsMap.productPriceAfterDiscountSR}
                </span>
              </span>
            </span>
          ) : (
            <span data-hook={DataHook.PriceRange} className={classNames(s.priceFrom, s.priceRangeFromTo)}>
              {prefix}
              {shouldRenderSecondaryPrice &&
                getSecondaryPrice({
                  product,
                  textsMap,
                  shouldEnableDiscountAndRegularPriceSwapViewer,
                  selectedVariant,
                  clientFormattedSecondaryPrice,
                })}
              {displayFromPrice}
              {suffix}
              <span className={a11y.srOnly} data-hook={DataHook.SrPriceRange}>
                {textsMap.productPriceAfterDiscountSR}
              </span>
            </span>
          ))}
        {!shouldAddDiscountsToVariantsGallery && (
          <>
            <span className={a11y.srOnly} data-hook={DataHook.SrPriceRange}>
              {textsMap.productPriceWhenThereIsNoDiscountSR}
            </span>
            <span data-hook={DataHook.PriceRange} className={classNames(s.priceFrom)}>
              {t('priceRangeText', {formattedAmount: displayFromPrice})}
            </span>
          </>
        )}
      </>
    );
  }
}

const PriceRange = withGlobals(withTranslations()(PriceRangeComp));

const RegularPrice = ({
  product,
  textsMap,
  shouldEnableDiscountAndRegularPriceSwapViewer,
  selectedVariant,
  includeVariantDiscount,
  clientFormattedPrimaryPrice,
  clientFormattedSecondaryPrice,
}: {
  product: IProduct;
  textsMap: TextMapProps;
  shouldEnableDiscountAndRegularPriceSwapViewer: boolean;
  selectedVariant: IProductItem;
  includeVariantDiscount?: boolean;
  clientFormattedPrimaryPrice?: string;
  clientFormattedSecondaryPrice?: string;
}) => {
  const hasDiscount = isProductItemHasDiscount(product, selectedVariant);
  const primaryPrice = getPrimaryPrice({
    product,
    selectedVariant,
    includeVariantDiscount,
    clientFormattedPrimaryPrice,
  });

  return (
    <>
      {hasDiscount &&
        getSecondaryPrice({
          product,
          textsMap,
          shouldEnableDiscountAndRegularPriceSwapViewer,
          selectedVariant,
          clientFormattedSecondaryPrice,
        })}
      {
        <>
          <span className={a11y.srOnly} data-hook={DataHook.SrPriceToPay}>
            {hasDiscount ? textsMap.productPriceAfterDiscountSR : textsMap.productPriceWhenThereIsNoDiscountSR}
          </span>
          <span
            data-hook={DataHook.PriceToPay}
            className={classNames({
              [s.salePrice]: hasDiscount && shouldEnableDiscountAndRegularPriceSwapViewer,
              [s.oldSalePrice]: hasDiscount && !shouldEnableDiscountAndRegularPriceSwapViewer,
              [s.priceToPay]: !hasDiscount,
            })}
            data-wix-price={primaryPrice}>
            {primaryPrice}
          </span>
        </>
      }
    </>
  );
};

function getSecondaryPrice({
  product,
  textsMap,
  shouldEnableDiscountAndRegularPriceSwapViewer,
  selectedVariant,
  clientFormattedSecondaryPrice,
}: {
  product: IProduct;
  textsMap: TextMapProps;
  shouldEnableDiscountAndRegularPriceSwapViewer: boolean;
  selectedVariant: IProductItem;
  clientFormattedSecondaryPrice?: string;
}) {
  const selectedSecondaryPrice = clientFormattedSecondaryPrice || formattedSecondaryPrice(product, selectedVariant);
  return getPriceBeforeDiscount({
    textsMap,
    secondaryPrice: selectedSecondaryPrice,
    shouldEnableDiscountAndRegularPriceSwapViewer,
  });
}

function getPrimaryPrice({
  product,
  selectedVariant,
  includeVariantDiscount,
  clientFormattedPrimaryPrice,
}: {
  product: IProduct;
  selectedVariant: IProductItem;
  includeVariantDiscount?: boolean;
  clientFormattedPrimaryPrice?: string;
}) {
  if (clientFormattedPrimaryPrice) {
    return clientFormattedPrimaryPrice;
  }
  const itemDiscount = product.itemDiscount;
  return formattedPrimaryPrice({...product, itemDiscount}, selectedVariant, includeVariantDiscount);
}

class ProductPriceImpl extends React.Component<IProductPriceProps, any> {
  private renderPrice() {
    const {product, textsMap, fromPrice, globals, selectedProductVariant} = this.props;
    const shouldEnableDiscountAndRegularPriceSwapViewer = globals.useExperiments.enabled(
      Experiments.EnableDiscountAndRegularPriceSwapViewer
    );
    const includeVariantDiscount = globals.useExperiments.enabled(Experiments.AddDiscountsToVariantsItemsQueries);
    const clientFormatted = globals.clientFormattedPrices?.[product.id];
    return (
      <div
        className={classNames(s.prices, s.pricesMarginTop, {
          [s.pricesFlexProperties]: shouldEnableDiscountAndRegularPriceSwapViewer,
        })}
        data-hook={DataHook.PricesContainer}>
        {fromPrice ? (
          <PriceRange
            formattedFromPrice={fromPrice}
            textsMap={textsMap}
            product={product}
            selectedVariant={selectedProductVariant}
            clientFormattedSecondaryPrice={clientFormatted?.secondaryPrice}
            clientFromPrice={clientFormatted?.fromPrice}
          />
        ) : (
          <RegularPrice
            selectedVariant={selectedProductVariant}
            product={product}
            textsMap={textsMap}
            shouldEnableDiscountAndRegularPriceSwapViewer={shouldEnableDiscountAndRegularPriceSwapViewer}
            includeVariantDiscount={includeVariantDiscount}
            clientFormattedPrimaryPrice={clientFormatted?.primaryPrice}
            clientFormattedSecondaryPrice={clientFormatted?.secondaryPrice}
          />
        )}
      </div>
    );
  }

  public render() {
    const {
      product,
      allowFreeProducts,
      globals: {productsManifest},
    } = this.props;

    if (isProductUnavailable(productsManifest[product.id])) {
      return null;
    }

    const shouldRenderPriceLayout = shouldRenderPrice(product, allowFreeProducts);
    const shouldRenderOutOfStockLayout = shouldRenderOutOfStock(product);

    if (shouldRenderOutOfStockLayout) {
      return (
        <div className={s.priceContainer}>
          <ConditionalRender by={'showTextualAddToCartButton'} className={s.outOfStockPriceContainer}>
            {this.renderPrice()}
          </ConditionalRender>
        </div>
      );
    } else if (shouldRenderPriceLayout) {
      return <div className={s.priceContainer}>{this.renderPrice()}</div>;
    }
    return null;
  }
}

export const ProductPrice = withGlobals(ProductPriceImpl);
